
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import Head from "next/head";
import "../styles/globals.css";
import "../store";
import { pageview } from "../lib/ga";
import dynamic from "next/dynamic";
import { signInStatus } from "../redux/signIn";
import { setChessPartyMode, setLoadingBar } from "../redux/siteData";
import { setBoardSize, setSetting } from "../redux/settings";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Provider } from "hooks-for-redux";
import { ErrorBoundary } from "react-error-boundary";
import ThirdPartyMetricaProvider from "../components/common/third-party-metrica-provider";
// import posthog from "posthog-js-lite";
// import { PostHogProvider } from "posthog-js/react";
const Navigation = dynamic(() => import("../navigation/Navigation"));
const Footer = dynamic(() => import("../navigation/Footer"));
const Popup = dynamic(() => import("../components/ui/popup"));
const EmailActivationBanner = dynamic(() => import("../components/common/email-activation-banner"));
// import { GrowthBookProvider } from "@growthbook/growthbook-react";
// import { growthbook } from "../util/growthbook";
import { shouldLoadAdsForPath } from "../config/ads";
import { enableRussiaClientHealth, reportRussiaClientHealth, } from "../services/clientHealth";
import { setEmailNotActivated, userStore } from "@/redux/user";
import { markPlaywireSpaNavigation } from "@/lib/playwire";
import { BLOG_LOCALE, isEnglishOnlyContentPath } from "@/lib/blogLocale";
import { wsConnectionStore } from "@/redux/wsConnection";
import { isReadingRoute } from "@/services/websocketRoute";
const FALLBACK_HOST = "www.chesshotel.com";
const STALE_NEXT_BUILD_RELOAD_KEY = "chesshotel:stale-next-build-reload-at";
const STALE_NEXT_BUILD_RELOAD_WINDOW_MS = 30000;
const DISABLE_THIRD_PARTY = process.env.NEXT_PUBLIC_DISABLE_THIRD_PARTY === "true";
declare global {
    interface Window {
        gtag: any;
        googlefc: any;
        adsbygoogle: any;
        AppleID: any;
        fbAsyncInit: any;
        Stripe: any;
        __cmp: any;
        setCookie: any;
        __tcfapi: any;
        google: any;
        chesshotel: any;
        chesshotelPuzzle: any;
        fusetag: any;
        FB: any;
        dataLayer: any;
        google_optimize: any;
        ramp: any;
        yaContextCb: any;
        Ya: any;
        _pwGA4PageviewId: any;
        workbox: any;
        userHasInteracted: boolean;
        webkitAudioContext: any;
        requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        ym: any;
        __gameReviewFlag: boolean;
        __google_initialized: boolean;
        googletag: any;
        pbjs: any;
        __chesshotelAppHydrated: boolean;
    }
}
function getBaseUrl() {
    const host = process.env.NEXT_PUBLIC_HOST || FALLBACK_HOST;
    const withProtocol = host.startsWith("http://") || host.startsWith("https://")
        ? host
        : `https://${host}`;
    return withProtocol.endsWith("/") ? withProtocol.slice(0, -1) : withProtocol;
}
function getNormalizedConfiguredHost() {
    return (process.env.NEXT_PUBLIC_HOST || FALLBACK_HOST)
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "")
        .toLowerCase();
}
function isSearchIndexableEnvironment() {
    return (process.env.NEXT_PUBLIC_TEST !== "true" &&
        getNormalizedConfiguredHost() === FALLBACK_HOST);
}
function normalizePath(path: string) {
    if (!path)
        return "/";
    const withoutHash = path.split("#")[0];
    const withoutQuery = withoutHash.split("?")[0];
    if (!withoutQuery)
        return "/";
    const withSlash = withoutQuery.startsWith("/")
        ? withoutQuery
        : `/${withoutQuery}`;
    if (withSlash.length > 1 && withSlash.endsWith("/")) {
        return withSlash.slice(0, -1);
    }
    return withSlash;
}
function stripLocalePrefix(path: string, locale: string | undefined, defaultLocale: string) {
    if (!locale || locale === defaultLocale)
        return path;
    const prefix = `/${locale}`;
    if (path === prefix)
        return "/";
    if (path.startsWith(`${prefix}/`)) {
        return path.slice(prefix.length);
    }
    return path;
}
function buildLocalePath(locale: string, path: string, defaultLocale: string) {
    if (locale === defaultLocale)
        return path;
    if (path === "/")
        return `/${locale}`;
    return `/${locale}${path}`;
}
function materializeDynamicPath(path: string, query: Record<string, string | string[] | undefined>) {
    const routeValue = (key: string, catchAll = false) => {
        const value = query[key];
        if (Array.isArray(value)) {
            const segments = catchAll ? value : value.slice(0, 1);
            return segments.map((segment) => encodeURIComponent(segment)).join("/");
        }
        return value ? encodeURIComponent(value) : "";
    };
    const resolved = path
        .replace(/\[\[\.\.\.([^\]]+)\]\]/g, (_, key) => routeValue(key, true))
        .replace(/\[\.\.\.([^\]]+)\]/g, (_, key) => routeValue(key, true))
        .replace(/\[([^\]]+)\]/g, (_, key) => routeValue(key));
    return normalizePath(resolved.replace(/\/{2,}/g, "/"));
}
const EXACT_NOINDEX_FOLLOW_PATHS = new Set([
    "/admin",
    "/analysis",
    "/analysis-board",
    "/c",
    "/challenge",
    "/chessparty/game",
    "/chessparty/game-review",
    "/chessparty/lobby",
    "/chessparty/tv",
    "/claim/chessparty",
    "/friends",
    "/game",
    "/game-review",
    "/iframe",
    "/kindle-debug",
    "/leagues",
    "/lobby",
    "/membership-done",
    "/newpuzzle",
    "/players",
    "/premium/welcome",
    "/profile",
    "/register",
    "/settings",
    "/signIn",
    "/user",
]);
const PREFIX_NOINDEX_FOLLOW_PATHS = [
    "/admin/",
    "/analysis/",
    "/c/",
    "/challenge/",
    "/chessparty/game/",
    "/chessparty/game-review/",
    "/daily/game/",
    "/friends/",
    "/game/",
    "/game-review/",
    "/iframe/",
    "/leagues/",
    "/newpuzzle/",
    "/puzzles/mistakes/practice",
    "/misstake-puzzle",
    "/profile/",
    "/puzzle/",
    "/register/",
    "/settings/",
    "/signIn/",
    "/user/",
];
function isNoindexFollowPath(path: string) {
    return (EXACT_NOINDEX_FOLLOW_PATHS.has(path) ||
        PREFIX_NOINDEX_FOLLOW_PATHS.some((prefix) => path.startsWith(prefix)));
}
function getErrorText(error: unknown) {
    if (!error)
        return "";
    if (typeof error === "string")
        return error;
    if (error instanceof Error) {
        return `${error.name} ${error.message} ${error.stack || ""}`;
    }
    try {
        return JSON.stringify(error);
    }
    catch (_) {
        return String(error);
    }
}
function isStaleNextBuildError(error: unknown) {
    const routeError = error as {
        cancelled?: boolean;
        code?: string;
        name?: string;
        message?: string;
    };
    if (routeError?.cancelled)
        return false;
    const code = routeError?.code;
    if (code === "PAGE_LOAD_ERROR" || code === "ENOENT")
        return true;
    const errorText = getErrorText(error).toLowerCase();
    return [
        "chunkloaderror",
        "loading chunk",
        "loading css chunk",
        "failed to load static props",
        "failed to load static file",
        "missing static page",
        "/_next/data/",
        "/_next/static/",
        "_buildmanifest",
        "__build_manifest",
    ].some((needle) => errorText.includes(needle));
}
function reloadAfterStaleNextBuild(url?: string) {
    if (typeof window === "undefined")
        return;
    const now = Date.now();
    try {
        const lastReload = Number(window.sessionStorage.getItem(STALE_NEXT_BUILD_RELOAD_KEY) || "0");
        if (now - lastReload < STALE_NEXT_BUILD_RELOAD_WINDOW_MS)
            return;
        window.sessionStorage.setItem(STALE_NEXT_BUILD_RELOAD_KEY, String(now));
    }
    catch (_) {
        // sessionStorage can be unavailable in private browsing modes.
    }
    if (url && url.startsWith("/")) {
        window.location.assign(url);
        return;
    }
    window.location.reload();
}
function initSettings() {
    try {
        const chessSet = localStorage.getItem("chessSet");
        if (chessSet) {
            setSetting({ name: "chessSet", val: chessSet });
        }
    }
    catch (e) {
        console.log(e);
    }
}
function ErrorFallback({ error, resetErrorBoundary }) {
    return (<Popup onClose={null} title="Something went wrong">
      <div className="flex flex-col items-center justify-center w-full pt-4 mt-4">
        <div className="mb-6">{error.message}</div>
        <button className="btn btn-base btn-blue" onClick={resetErrorBoundary}>
          Try again
        </button>
      </div>
    </Popup>);
}
const myErrorHandler = (error: Error, info: {
    componentStack: string;
}) => {
    import("../redux/ping").then((module) => {
        const { setError, setErrorData, ERROR_BOUNDRY, sendPingData } = module;
        setError(ERROR_BOUNDRY);
        setErrorData({
            errorMsg: "version9 " + error.message,
            componentStack: info.componentStack,
        });
        sendPingData();
    });
};
// let lastUrl: string | null = null;
function MyApp({ Component, pageProps, userData }) {
    const router = useRouter();
    const defaultLocale = router.defaultLocale || "en";
    const currentLocale = router.locale || defaultLocale;
    const locales = router.locales && router.locales.length > 0
        ? router.locales.filter(Boolean)
        : [defaultLocale];
    const isSearchIndexable = isSearchIndexableEnvironment();
    const baseUrl = isSearchIndexable ? getBaseUrl() : `https://${FALLBACK_HOST}`;
    const normalizedPath = normalizePath(router.asPath || "/");
    const pathWithoutLocale = materializeDynamicPath(stripLocalePrefix(normalizedPath, currentLocale, defaultLocale), router.query);
    const isEnglishOnlyContent = isEnglishOnlyContentPath(pathWithoutLocale);
    const isLegalPath = pathWithoutLocale === "/termsofservice" ||
        pathWithoutLocale === "/privacypolicy";
    const canonicalLocale = isEnglishOnlyContent
        ? BLOG_LOCALE
        : isLegalPath && currentLocale !== defaultLocale
            ? defaultLocale
            : currentLocale;
    const canonicalPath = buildLocalePath(canonicalLocale, pathWithoutLocale, defaultLocale);
    const canonicalUrl = `${baseUrl}${canonicalPath}`;
    const shouldRenderAlternates = isSearchIndexable &&
        !isEnglishOnlyContent &&
        (!isLegalPath || currentLocale === defaultLocale);
    const robotsContent = !isSearchIndexable
        ? "noindex,nofollow,noarchive"
        : isNoindexFollowPath(pathWithoutLocale)
            ? "noindex,follow"
            : null;
    const xDefaultHref = `${baseUrl}${buildLocalePath(defaultLocale, pathWithoutLocale, defaultLocale)}`;
    const currentPath = router.pathname;
    const gamePath = currentPath.startsWith("/game") ||
        currentPath.startsWith("/chessparty/game") ||
        currentPath.startsWith("/daily/game") ||
        currentPath.startsWith("/analysis");
    const validateEmailPath = currentPath.startsWith("/register/validateEmail");
    // const gameReviewPath = currentPath.startsWith("/game-review");
    const adminPath = currentPath.startsWith("/admin");
    const tvPath = currentPath === "/chessparty/tv";
    const noWsConnect = validateEmailPath || tvPath || adminPath;
    const iframePath = currentPath.startsWith("/iframe");
    const lessonPath = currentPath.startsWith("/learn");
    const hideFooter = gamePath ||
        iframePath ||
        validateEmailPath ||
        lessonPath ||
        tvPath ||
        adminPath;
    const hideNavigation = iframePath || validateEmailPath || tvPath || adminPath;
    useEffect(() => {
        setChessPartyMode(currentPath.startsWith("/chessparty"));
    }, [currentPath]);
    useEffect(() => {
        window.__chesshotelAppHydrated = true;
        window.dispatchEvent(new Event("chesshotel:app-hydrated"));
        if (enableRussiaClientHealth({ source: "app-hydration" })) {
            reportRussiaClientHealth("app_hydrated", { path: currentPath });
        }
        // Preload websocket module to reduce first-connect delay.
        void import("../services/websocket");
        if (!noWsConnect) {
            signInStatus();
        }
        initSettings();
        setBoardSize();
        if (!DISABLE_THIRD_PARTY) {
            void import("../idleLoadScripts").then(({ idleLoadScripts }) => {
                idleLoadScripts(currentPath);
            });
        }
        function onUserInteraction() {
            window.userHasInteracted = true;
            document.removeEventListener("click", onUserInteraction);
            document.removeEventListener("mousedown", onUserInteraction);
            document.removeEventListener("touchstart", onUserInteraction);
            // Dispatch custom event for deferred operations
            window.dispatchEvent(new Event("userHasInteracted"));
        }
        document.addEventListener("touchstart", onUserInteraction);
        document.addEventListener("mousedown", onUserInteraction);
        document.addEventListener("click", onUserInteraction);
        // Listen for email verification events from other tabs
        let emailVerifiedChannel: BroadcastChannel | null = null;
        if (typeof BroadcastChannel !== "undefined") {
            emailVerifiedChannel = new BroadcastChannel("chesshotel-email-verified");
            emailVerifiedChannel.onmessage = (event) => {
                if (event.data?.type === "EMAIL_VERIFIED") {
                    // Refresh user status to update emailNotActivated flag
                    setEmailNotActivated(false);
                }
            };
        }
        return () => {
            document.removeEventListener("click", onUserInteraction);
            document.removeEventListener("mousedown", onUserInteraction);
            document.removeEventListener("touchstart", onUserInteraction);
            if (emailVerifiedChannel) {
                emailVerifiedChannel.close();
            }
        };
    }, []);
    useEffect(() => {
        // posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        //   api_host:
        //     process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
        //   autocapture: false,
        //   capture_pageview: false,
        //   person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
        //   // Enable debug mode in development
        //   loaded: (posthog) => {
        //     if (process.env.NODE_ENV === "development") posthog.debug();
        //   },
        // });
        // lastUrl = currentPath;
        let routeStartTime: number;
        let loadingBarTimeout: NodeJS.Timeout | null = null;
        const handleStart = (url: string) => {
            routeStartTime = performance.now();
            reportRussiaClientHealth("route_change_start", { url });
            // Only show loading bar if navigation takes longer than 100ms
            loadingBarTimeout = setTimeout(() => setLoadingBar(true), 100);
        };
        const handleStop = (url: string) => {
            markPlaywireSpaNavigation();
            // Clear timeout and hide loading bar
            if (loadingBarTimeout) {
                clearTimeout(loadingBarTimeout);
                loadingBarTimeout = null;
            }
            setLoadingBar(false);
            if (!DISABLE_THIRD_PARTY) {
                pageview(url);
            }
            reportRussiaClientHealth("route_change_complete", {
                url,
                durationMs: typeof routeStartTime === "number"
                    ? Math.round(performance.now() - routeStartTime)
                    : null,
            });
            // Use centralized path checking for ads
            if (!DISABLE_THIRD_PARTY && shouldLoadAdsForPath(url)) {
                void import("../loadAds").then(({ loadAds }) => {
                    loadAds();
                });
            }
            // A tab opened on an article has no live socket. Connect when it later
            // navigates into the app, unless another tab currently owns the socket.
            if (!isReadingRoute(url) &&
                userStore.getState().username &&
                !wsConnectionStore.getState().otherLoginShow) {
                void import("../services/websocket").then(({ websocket, initWebsocketConnect }) => {
                    if (!websocket || websocket.readyState >= WebSocket.CLOSING) {
                        initWebsocketConnect();
                    }
                });
            }
            // If leaving game-review pages, (re)connect WS when needed
            // if (
            //   lastUrl.includes("/game-review") &&
            //   !url.includes("/game-review") &&
            //   !websocket
            // ) {
            //   initWebsocketConnect();
            // }
            // posthog?.capture("$pageview");
            // growthbook.setURL(window.location.href);
            // lastUrl = url;
        };
        const handleError = (error: unknown, url?: string) => {
            if (loadingBarTimeout) {
                clearTimeout(loadingBarTimeout);
                loadingBarTimeout = null;
            }
            setLoadingBar(false);
            if (isStaleNextBuildError(error)) {
                reportRussiaClientHealth("stale_next_build_reload", {
                    url,
                    message: getErrorText(error).slice(0, 400),
                });
                reloadAfterStaleNextBuild(url);
            }
        };
        let isInitialLoad = true;
        const handleRouteChange = (url: string) => {
            // Handle Playwire SPA navigation - skip for initial load
            if (isInitialLoad) {
                isInitialLoad = false;
                return;
            }
            // if (window.ramp && typeof window.ramp.spaNewPage === 'function') {
            //   try {
            //     // Determine path based on the new route
            //     const path = url.includes('/players') ? '/players' : 'ROS';
            //     console.log('Calling spaNewPage for route change to:', url, 'with path:', path);
            //     window.ramp.spaNewPage(path);
            //   } catch (error) {
            //     console.log('Playwire spaNewPage error:', error);
            //   }
            // }
        };
        const handleComplete = (url: string) => {
            handleStop(url);
            handleRouteChange(url);
        };
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            if (isStaleNextBuildError(event.reason)) {
                reportRussiaClientHealth("stale_next_build_rejection", {
                    message: getErrorText(event.reason).slice(0, 400),
                });
                reloadAfterStaleNextBuild();
            }
        };
        const handleWindowError = (event: ErrorEvent) => {
            const target = event.target as HTMLScriptElement | HTMLLinkElement | null;
            const assetUrl = target && "src" in target && target.src
                ? target.src
                : target && "href" in target && target.href
                    ? target.href
                    : "";
            if (isStaleNextBuildError(event.error || event.message || assetUrl) ||
                assetUrl.includes("/_next/static/")) {
                reportRussiaClientHealth("stale_next_build_window_error", {
                    message: event.message,
                    assetUrl,
                });
                reloadAfterStaleNextBuild();
            }
        };
        router.events.on("routeChangeStart", handleStart);
        router.events.on("routeChangeComplete", handleComplete);
        router.events.on("routeChangeError", handleError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);
        window.addEventListener("error", handleWindowError, true);
        return () => {
            if (loadingBarTimeout) {
                clearTimeout(loadingBarTimeout);
            }
            router.events.off("routeChangeStart", handleStart);
            router.events.off("routeChangeComplete", handleComplete);
            router.events.off("routeChangeError", handleError);
            window.removeEventListener("unhandledrejection", handleUnhandledRejection);
            window.removeEventListener("error", handleWindowError, true);
        };
    }, [router.events]);
    const appShell = (<>
      <Head>
        <meta name="viewport" content="viewport-fit=cover, width=device-width, height=device-height, initial-scale=1, shrink-to-fit=no, maximum-scale=1, user-scalable=no"/>
        <link rel="canonical" href={canonicalUrl}/>
        <meta property="og:image" content={`${baseUrl}/static/fb-icon-white.png`} key="og-image"/>
        {!DISABLE_THIRD_PARTY && shouldLoadAdsForPath(currentPath) && (<>
            <link rel="dns-prefetch" href="//cdn.intergient.com"/>
            <link rel="preconnect" href="https://cdn.intergient.com" crossOrigin="anonymous"/>
            <link rel="preconnect" href="https://securepubads.g.doubleclick.net" crossOrigin="anonymous"/>
          </>)}
        {robotsContent && <meta name="robots" content={robotsContent}/>}
        {shouldRenderAlternates &&
            locales.map((locale) => {
                const href = `${baseUrl}${buildLocalePath(locale, pathWithoutLocale, defaultLocale)}`;
                return (<link rel="alternate" hrefLang={locale} href={href} key={`alt-${locale}`}/>);
            })}
        {shouldRenderAlternates && (<link rel="alternate" hrefLang="x-default" href={xDefaultHref} key="alt-x-default"/>)}
      </Head>
      {!hideNavigation && <Navigation />}
      {!adminPath && <EmailActivationBanner />}
      <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => {
            window.location.reload();
        }} onError={myErrorHandler}>
        {/* <GrowthBookProvider growthbook={growthbook}> */}
        <Component {...pageProps}/>
        {/* </GrowthBookProvider> */}
      </ErrorBoundary>
      {!hideFooter && <Footer />}
    </>);
    return (<>
      {/*
        //@ts-ignore */}
      <Provider>
        {DISABLE_THIRD_PARTY ? (appShell) : (<ThirdPartyMetricaProvider>{appShell}</ThirdPartyMetricaProvider>)}
      </Provider>
    </>);
}
const __Next_Translate__Page__1a0fc0d4fb5__ = MyApp;

    export default __appWithI18n(__Next_Translate__Page__1a0fc0d4fb5__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  